import { MutationTree } from 'vuex'
import orderBy from 'lodash/orderBy'
import get from 'lodash/get'

import { CreateSharedFolderSuccess, SharedFolder } from '@/types'
import { SharedFoldersState } from './sharedFolder.types'
import { splitSortOption } from '@/common/splitSortOption'
import { GetSharedFolderResponse } from 'vue-common/src/generated'

const addTableDialogAndMenu = <T>(data: Array<T>) =>
  data.map(item => ({ ...item, remove: false, share: false, menu: false }))

export enum MutationTypes {
  SHARED_FOLDER_REQUEST = 'SHARED_FOLDER_REQUEST',
  SET_SHARED_FOLDER = 'SET_SHARED_FOLDER',
  REMOVE_SHARED_FOLDER = 'REMOVE_SHARED_FOLDER',
  SET_SHARED_FOLDER_DELETE_ERROR = 'SET_SHARED_FOLDER_DELETE_ERROR',
  SET_SHARED_FOLDER_UPDATE_ERROR = 'SET_SHARED_FOLDER_UPDATE_ERROR',
  SET_SHARED_FOLDER_GET_ERROR = 'SET_SHARED_FOLDER_GET_ERROR',
  SHARED_FOLDERS_REQUEST = 'SHARED_FOLDERS_REQUEST',
  SET_SHARED_FOLDERS = 'SET_SHARED_FOLDERS',
  SET_SHARED_FOLDERS_GET_ERROR = 'SET_SHARED_FOLDERS_GET_ERROR',
  ADD_SHARED_FOLDER = 'ADD_SHARED_FOLDER',
  SET_SORT_OPTION = 'SET_SORT_OPTION',
  CREATE_FOLDER_REQUEST = 'CREATE_FOLDER_REQUEST',
  CREATE_FOLDER_SUCCESS = 'CREATE_FOLDER_SUCCESS',
  CREATE_FOLDER_FAILURE = 'CREATE_FOLDER_FAILURE',
  SET_SORTED_FOLDERS = 'SET_SORTED_FOLDERS',
  SET_SORTED_FOLDERS_GET_ERROR = 'SET_SORTED_FOLDERS_GET_ERROR',
  SHARED_FOLDER_TECHPACKS_REQUEST = 'SHARED_FOLDER_TECHPACKS_REQUEST',
  SET_SHARED_FOLDER_TECHPACKS = 'SET_SHARED_FOLDER_TECHPACKS',
  SET_SHARED_FOLDER_TECHPACKS_ERROR = 'SET_SHARED_FOLDER_TECHPACKS_ERROR',
  REMOVE_TECHPACK = 'REMOVE_TECHPACK',
  REMOVE_TECHPACK_ERROR = 'REMOVE_TECHPACK_ERROR',
  SET_SIGNED_URL_ERROR = 'SET_SIGNED_URL_ERROR',
  SET_TECHPACK_SHARE_LINK = 'SET_TECHPACK_SHARE_LINK',
  SET_TECHPACK_SHARE_LINK_ERROR = 'SET_TECHPACK_SHARE_LINK_ERROR',
  SEND_PUBLIC_LINK_EMAIL_ERROR = 'SEND_PUBLIC_LINK_EMAIL_ERROR',
  TOGGLE_FOLDER_MENU = 'TOGGLE_FOLDER_MENU',
  TOGGLE_TECHPACK_MENU = 'TOGGLE_TECHPACK_MENU'
}

export const mutations: MutationTree<SharedFoldersState> = {
  [MutationTypes.SHARED_FOLDER_REQUEST](state) {
    state.isFetching = true
  },
  [MutationTypes.SET_SHARED_FOLDER](state, folder: GetSharedFolderResponse) {
    state.isFetching = false
    state.sharedFolder = { ...folder, techpacks: addTableDialogAndMenu(folder.techpacks) }
  },
  [MutationTypes.REMOVE_SHARED_FOLDER](state) {
    state.sharedFolder = undefined
  },
  [MutationTypes.SET_SHARED_FOLDER_UPDATE_ERROR](state, error: Error) {
    state.updateFolderError = true
    state.error = error
  },
  [MutationTypes.SET_SHARED_FOLDER_GET_ERROR](state, error: Error) {
    state.isFetching = false
    state.getFolderError = true
    state.error = error
  },
  [MutationTypes.SHARED_FOLDERS_REQUEST](state) {
    state.isFetching = true
  },
  [MutationTypes.SET_SHARED_FOLDERS](state, folders: Array<SharedFolder>) {
    state.isFetching = false
    state.sharedFolders = addTableDialogAndMenu(folders)
  },
  [MutationTypes.SET_SHARED_FOLDERS_GET_ERROR](state) {
    state.isFetching = false
    state.getFoldersError = true
  },
  [MutationTypes.ADD_SHARED_FOLDER](state, folder: SharedFolder) {
    const { option, direction } = splitSortOption<SharedFolder>(state.sortOption)

    state.sharedFolders.push(folder)
    state.sharedFolders = addTableDialogAndMenu(orderBy(state.sharedFolders, option, direction))
  },
  [MutationTypes.SET_SORT_OPTION](state, sortOption: string) {
    state.sortOption = sortOption
  },

  [MutationTypes.CREATE_FOLDER_REQUEST](state) {
    state.isCreating = true
    state.createFolderError = false
  },
  [MutationTypes.CREATE_FOLDER_SUCCESS](state, result: CreateSharedFolderSuccess) {
    state.isCreating = false
    const { option, direction } = splitSortOption<SharedFolder>(state.sortOption)

    state.sharedFolder = { ...result.folder, techpacks: addTableDialogAndMenu(result.folder.techpacks) }
    state.sharedFolders = addTableDialogAndMenu(orderBy(result.folders, option, direction))
    state.createFolderError = false
  },
  [MutationTypes.CREATE_FOLDER_FAILURE](state, error: Error) {
    state.isCreating = false
    state.createFolderError = true
    state.error = error
  },

  [MutationTypes.SET_SORTED_FOLDERS](state, folders: Array<SharedFolder>) {
    state.sharedFolders = addTableDialogAndMenu(folders)
  },
  [MutationTypes.SET_SORTED_FOLDERS_GET_ERROR](state) {
    state.getSortedFoldersError = true
  },
  [MutationTypes.SHARED_FOLDER_TECHPACKS_REQUEST](state) {
    state.isFetching = true
  },
  [MutationTypes.SET_SHARED_FOLDER_TECHPACKS](state, techpacks) {
    if (state.sharedFolder) {
      state.isFetching = false
      state.sharedFolder.techpacks = addTableDialogAndMenu(techpacks)
    }
  },
  [MutationTypes.SET_SHARED_FOLDER_TECHPACKS_ERROR](state) {
    state.isFetching = false
    state.getTechpacksError = true
  },
  [MutationTypes.REMOVE_TECHPACK](state, techpackId: string) {
    if (state.sharedFolder?.techpacks?.length) {
      state.sharedFolder.techpacks = state.sharedFolder.techpacks.filter(
        (sharedTechpack: any) => sharedTechpack.techpack._id !== techpackId
      )
    }
  },
  [MutationTypes.REMOVE_TECHPACK_ERROR](state) {
    state.removeTechpackError = true
  },
  [MutationTypes.SET_SIGNED_URL_ERROR](state) {
    state.getSignedURLError = true
  },
  [MutationTypes.SET_TECHPACK_SHARE_LINK](state, { techpackId, share }) {
    if (state.sharedFolder) {
      const techpackIndex = state.sharedFolder?.techpacks?.findIndex(
        (sharedTechpack: any) => sharedTechpack.techpack._id === techpackId
      )
      if (techpackIndex !== undefined && techpackIndex > -1) {
        const tp = get(state.sharedFolder?.techpacks, techpackIndex, null)
        if (tp && tp.techpack) {
          if (typeof tp.techpack !== 'string') {
            tp.techpack.share = share
          }
        }
      }
    }
  },
  [MutationTypes.SET_TECHPACK_SHARE_LINK_ERROR](state) {
    state.getShareLinkError = true
  },
  [MutationTypes.SEND_PUBLIC_LINK_EMAIL_ERROR](state) {
    state.emailPublicLinkError = true
  },
  [MutationTypes.TOGGLE_FOLDER_MENU](state, { id, remove = false, share = false, menu = false }) {
    state.sharedFolders = state.sharedFolders.map(sharedFolder => {
      if (sharedFolder._id === id) {
        return {
          ...sharedFolder,
          remove,
          share,
          menu
        }
      }
      return sharedFolder
    })
  },
  [MutationTypes.TOGGLE_TECHPACK_MENU](state, { id, remove = false, share = false, menu = false }) {
    if (state.sharedFolder) {
      state.sharedFolder = {
        ...state.sharedFolder,
        techpacks: state.sharedFolder?.techpacks?.map(tp => {
          if (typeof tp.techpack !== 'string' && tp.techpack._id === id) {
            return {
              ...tp,
              remove,
              share,
              menu
            }
          }
          return tp
        })
      }
    }
  }
}
